import type {StorageLayerFactoryOptions} from '@packages/systems/cms/types';

export * from './fieldSchemas';

export const CONSISTENT_READ_OPTS: StorageLayerFactoryOptions = {
  readConsistencyLevel: 'consistent',
};

export const EVENTUAL_READ_OPTS: StorageLayerFactoryOptions = {
  readConsistencyLevel: 'eventual',
};

export const CMS_PLUGIN_KEY = 'cms' as const;

// Knock workflow key for CMS items export notifications
export const CMS_ITEMS_EXPORT_WORKFLOW_ID = 'cms-items-export' as const;

export const API_ERROR_MESSAGES = {
  COLLECTION_FIELDS_USED:
    'This collection has fields used by either a Collection List on the site or inside a Collection Page and cannot be deleted. Use the View Connections modal to easily locate and remove these connections.',
} as const;

export const ITEM_EDIT_SAVE_TYPES = Object.freeze({
  PUBLISH_NOW: 'Publish now',
  QUEUE_TO_PUBLISH: 'Queue to publish',
  REMOVE_FROM_QUEUE: 'Remove from queue',
  REPUBLISH: 'Republish',
  RESCHEDULE: 'Reschedule',
  REVERT_TO_DRAFT: 'Revert to draft',
  SAVE_CHANGES: 'Save changes',
  SAVE_DRAFT: 'Save draft',
  SCHEDULE: 'Schedule',
  UNPUBLISH: 'Unpublish',
});

export const ITEM_AUTOSAVE_ACTIONS = Object.freeze({
  PUBLISH_NOW: 'Publish now',
  QUEUE_FOR_PUBLISH: 'Queue for next site publish',
  REMOVE_FROM_QUEUE: 'Remove from queue',
  RESCHEDULE: 'Reschedule',
  SCHEDULE: 'Schedule to publish later',
  UNPUBLISH: 'Unpublish',
  UNSCHEDULE: 'Unschedule and save as draft',
  SAVE_DRAFT: 'Create draft',
});

export const ITEM_AUTOSAVE_MENU_ITEM_NOTE = Object.freeze({
  PUBLISH_NOW: 'Item will go live immediately.',
  QUEUE_DRAFT_ITEM: 'Item will go live the next time the site is published.',
  QUEUE_PUBLISHED_ITEM:
    'Changes will go live the next time the site is published.',
  REMOVE_DRAFT_ITEM_FROM_QUEUE:
    'Remove the item from publishing queue and save it as a draft.',
  REMOVE_PUBLISHED_ITEM_FROM_QUEUE:
    'Keep the item live on site, but prevent new changes from being published.',
  RESCHEDULE_ITEM: 'Change the scheduled publish date and time.',
  SCHEDULE_ITEM: 'Choose a future date and time for this item to auto-publish.',
  UNPUBLISH_ITEM:
    'Immediately remove this item from live site and revert to draft.',
  UNSCHEDULE_ITEM:
    'Cancel the scheduled publish and save this item as a draft.',
});

export const CMS_ITEM_STATUS = Object.freeze({
  NEW: undefined,
  DRAFT: 'Draft',
  CHANGES_IN_DRAFT: 'Changes in draft',
  PUBLISHED: 'Published',
  STAGED: 'Queued to publish',
  ARCHIVED: 'Archived',
});

// return the values of CMS_ITEM_STATUS as a union type
export type CmsItemStatusValues =
  (typeof CMS_ITEM_STATUS)[keyof typeof CMS_ITEM_STATUS];

// statuses that are ready to be published (not published or draft)
export const READY_STAGED_STATUSES = [
  'scheduled',
  'stagedChanges',
  'staged',
] as const;

export type ReadyStagedStatus = (typeof READY_STAGED_STATUSES)[number];

export const CMS_CHANGES_IN_DRAFT_LABEL = 'Changes in draft';

export const CMS_ANALYTICS_PAGE_TYPE = 'cms_data_manager';

// The delay added to the onChange debounce in ItemEditPane and RichTextInput.
export const CMS_DM_AUTOSAVE_DEBOUNCE_TIMING = 500;
// The delay added to the onChange throttle in ItemEditPane and RichTextInput. So only 1 save is allowed in this time.
export const CMS_DM_AUTOSAVE_THROTTLE_TIMING = 10000;
// The delay added to the debounce that calls the new item creation.
export const CMS_DM_AUTOSAVE_NEW_ITEM_DEBOUNCE_TIMING = 1500;
// The delay added to form changes in ItemEditPane.
export const CMS_DM_FORM_CHANGE_DEBOUNCE_TIMING = 300;
// Which field types within CMS items should be debounced when changes are made.
export const CMS_DM_FORM_FIELD_TYPES_TO_DEBOUNCE = new Set([
  'PlainText',
  'Number',
  'Color',
  'Video',
  'Link',
  'Email',
  'Phone',
]);
